/**
 * 动作配置编辑器
 * 支持玩家动作、系统动作、AI行为配置
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Switch,
  Divider,
  message,
} from 'antd'
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  RobotOutlined,
} from '@ant-design/icons'
import type { Table } from 'antd'

const { TextArea } = Input
const { Text, Title } = Typography

// 动作类型选项
const ACTION_TYPE_OPTIONS = [
  { value: 'player', label: '玩家动作', color: 'blue' },
  { value: 'system', label: '系统动作', color: 'green' },
  { value: 'npc', label: 'NPC动作', color: 'purple' },
  { value: 'environment', label: '环境动作', color: 'orange' },
]

// 触发类型选项
const TRIGGER_TYPE_OPTIONS = [
  { value: 'manual', label: '手动触发' },
  { value: 'auto', label: '自动触发' },
  { value: 'event', label: '事件触发' },
  { value: 'condition', label: '条件触发' },
  { value: 'time', label: '时间触发' },
  { value: 'gyroscope', label: '陀螺仪触发' },
]

// 目标类型选项
const TARGET_TYPE_OPTIONS = [
  { value: 'self', label: '自身' },
  { value: 'player', label: '玩家' },
  { value: 'npc', label: 'NPC' },
  { value: 'location', label: '地点' },
  { value: 'global', label: '全局' },
]

interface ActionEditorFormProps {
  initialData?: Record<string, any>
  onSave: (data: Record<string, any>) => Promise<void>
  loading?: boolean
}

// 主组件
const ActionEditorForm: React.FC<ActionEditorFormProps> = ({
  initialData,
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        key: initialData.key || initialData.id,
        name: initialData.name,
        description: initialData.description,
        action_type: initialData.type,
        trigger_type: initialData.trigger,
        target_type: initialData.target,
        cooldown: initialData.cooldown,
        cost: initialData.cost,
      })
    } else {
      form.resetFields()
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const actionData = {
        id: values.key || values.id,
        key: values.key,
        name: values.name,
        description: values.description,
        type: values.action_type,
        trigger: values.trigger_type,
        target: values.target_type,
        cooldown: values.cooldown,
        cost: values.cost,
        effects: initialData?.effects || [],
      }

      await onSave(actionData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <ThunderboltOutlined style={{ color: '#fa8c16' }} />
          <span>{initialData?.name || '新建动作'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
          {initialData?.type && <Tag color={ACTION_TYPE_OPTIONS.find((o) => o.value === initialData?.type)?.color}>
            {ACTION_TYPE_OPTIONS.find((o) => o.value === initialData?.type)?.label}
          </Tag>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="动作键" rules={[{ required: true }]}>
                  <Input placeholder="例如: action_shoot" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="动作名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 开枪" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="action_type" label="动作类型" rules={[{ required: true }]}>
                  <Select placeholder="选择动作类型" options={ACTION_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={2} placeholder="动作的功能描述..." />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="trigger_type" label="触发类型">
                  <Select placeholder="选择触发类型" options={TRIGGER_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="target_type" label="目标类型">
                  <Select placeholder="选择目标类型" options={TARGET_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="icon" label="图标">
                  <Input placeholder="icons/actions/..." />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="冷却与消耗" key="cost">
            <Divider>冷却时间</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="cooldown" label="冷却时间 (秒)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="max_charges" label="最大充能">
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="charge_rate" label="充能速率 (秒/次)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>

            <Divider>消耗资源</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="cost_stamina" label="体力消耗">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="cost_mana" label="魔法消耗">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="cost_ammo" label="弹药消耗">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="cost_gold" label="金币消耗">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="效果配置" key="effects">
            <Alert
              message="效果配置说明"
              description="动作执行后产生的效果，包括伤害、治疗、状态变化等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="effects" label="效果配置 (JSON)">
              <TextArea
                rows={10}
                placeholder={`[
  {
    "type": "damage",
    "target": "enemy",
    "value": 25,
    "damage_type": "physical"
  },
  {
    "type": "status",
    "target": "self",
    "status": "reloading",
    "duration": 2000
  }
]`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="条件限制" key="requirements">
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text>配置动作执行的前置条件，如属性要求、物品要求、技能要求等。</Text>
            </Card>
            <Form.Item name="requirements" label="前置条件 (JSON)">
              <TextArea
                rows={10}
                placeholder={`{
  "attribute": {
    "health": {"min": 1, "max": 100},
    "stamina": {"min": 10}
  },
  "item": {
    "required": ["item_weapon"],
    "consumed": false
  },
  "skill": {
    "required": ["shooting"],
    "level": {"min": 1}
  },
  "location": {
    "required": ["loc_combat_zone"]
  }
}`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="陀螺仪配置" key="gyroscope">
            <Alert
              message="陀螺仪动作说明"
              description="通过手机摇一摇触发的动作配置。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['gyroscope', 'shake_intensity']} label="摇动强度">
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                    placeholder="70"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['gyroscope', 'shake_duration']} label="持续时间 (ms)">
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="1000"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['gyroscope', 'collaborative']} label="协作触发" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Form.Item name={['gyroscope', 'feedback']} label="触觉反馈配置">
              <Select
                mode="tags"
                placeholder="success, error, heartbeat"
                style={{ width: '100%' }}
              >
                <Option value="success">成功震动</Option>
                <Option value="error">错误震动</Option>
                <Option value="heartbeat">心跳震动</Option>
              </Select>
            </Form.Item>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs
const { Option } = Select
const { Alert } = Card

export default ActionEditorForm
